// src/components/SeaStatePanel.jsx
import React, { useMemo, useState } from "react";
import { BRAND } from "../config/brandConfig";
// NOTE: We rely on STEPS being pulled from the FrameworkWheel export file

// safe tokens
const C = {
  ink: BRAND.colors?.ink || BRAND.colors?.textDark || "#0B1A3C",
  ink70: BRAND.colors?.ink70 || BRAND.colors?.textMedium || "#667085",
  line: BRAND.colors?.line || BRAND.colors?.border || "#E5E7EB",
  card: BRAND.colors?.card || BRAND.colors?.panel || "#FFFFFF",
  brand: BRAND.colors?.brand || BRAND.colors?.accent || "#0B3C5D",
  brand10: BRAND.colors?.brand10 || "rgba(11,60,93,.08)",
  success: "#047857", // Darkened green for contrast
  warn: "#92400e", // Darkened yellow/orange for contrast
  danger: "#991b1b", // Darkened red for contrast
};
const SHADOW = BRAND.shadows?.sm || "0 2px 6px rgba(0,0,0,.10)";
const RING = "3px solid rgba(11,60,93,.40)";

function Gauge({ score }) {
  const angle = useMemo(() => -90 + (score / 100) * 180, [score]);
  const color = score >= 70 ? C.success : score >= 40 ? C.warn : C.danger;
  const r = 130; // Radius for the main arc
  const cx = 150; // Center X
  const cy = 140; // Center Y

  const toPt = (a, radius = r) => {
    const rad = a * (Math.PI / 180);
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };

  return (
    <svg width="300" height="160" viewBox="0 0 300 160" aria-hidden="true">
      {/* arc background */}
      <path
        d="M20,140 A130,130 0 0 1 280,140"
        fill="none"
        stroke={C.line}
        strokeWidth="16"
        strokeLinecap="round"
      />
      {/* bands */}
      <path
        d="M20,140 A130,130 0 0 1 120,52"
        fill="none"
        stroke={C.danger}
        strokeOpacity=".25"
        strokeWidth="16"
      />
      <path
        d="M120,52 A130,130 0 0 1 180,52"
        fill="none"
        stroke={C.warn}
        strokeOpacity=".25"
        strokeWidth="16"
      />
      <path
        d="M180,52 A130,130 0 0 1 280,140"
        fill="none"
        stroke={C.success}
        strokeOpacity=".25"
        strokeWidth="16"
      />
      {/* needle */}
      <g transform={`translate(${cx},${cy}) rotate(${angle})`}>
        <line
          x1="0"
          y1="0"
          x2="0"
          y2="-115"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
        />
        <circle cx="0" cy="0" r="6" fill={color} />
      </g>
      {/* labels */}
      <text x="20" y="152" fontSize="11" fill={C.ink70}>
        Heavy
      </text>
      <text x="142" y="152" fontSize="11" fill={C.ink70} textAnchor="middle">
        Choppy
      </text>
      <text x="280" y="152" fontSize="11" fill={C.ink70} textAnchor="end">
        Calm
      </text>
    </svg>
  );
}

function CheckItem({ label, checked, onChange }) {
  // ... (CheckItem component code)
  return (
    <label
      style={{
        display: "grid",
        gridTemplateColumns: "18px 1fr",
        gap: 10,
        alignItems: "center",
        cursor: "pointer",
        color: C.ink,
        fontSize: 14,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 16, height: 16 }}
        onFocus={(e) => (e.currentTarget.style.outline = RING)}
        onBlur={(e) => (e.currentTarget.style.outline = "none")}
      />
      <span>{label}</span>
    </label>
  );
}

function Slider({ label, min, max, step, value, onChange }) {
  // ... (Slider component code)
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 12,
          color: C.ink70,
          marginBottom: 4,
        }}
      >
        <span>{label}</span>
        <span style={{ opacity: 0.7 }}>
          {min}–{max}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%" }}
        onFocus={(e) => (e.currentTarget.style.outline = RING)}
        onBlur={(e) => (e.currentTarget.style.outline = "none")}
        aria-label={label}
      />
    </div>
  );
}

function SeaStateReadiness({ activeStepIndex }) {
  // Checklist
  const [ips, setIps] = useState(true);
  const [cash, setCash] = useState(true);
  const [estate, setEstate] = useState(false);
  const [cadence, setCadence] = useState(true);

  // Simulation
  const [drop, setDrop] = useState(10);
  const [incomeGap, setIncomeGap] = useState(0);

  // Scoring logic remains the same
  const checklistPoints =
    (ips ? 18 : 0) + (cash ? 22 : 0) + (estate ? 15 : 0) + (cadence ? 15 : 0);
  const simPenalty =
    Math.min(20, Math.max(0, drop - 10)) +
    Math.min(10, Math.max(0, incomeGap * 2));
  const score = Math.max(
    0,
    Math.min(100, checklistPoints + Math.max(0, 30 - simPenalty))
  );

  const band =
    score >= 70 ? "Calm Seas" : score >= 40 ? "Choppy" : "Heavy Seas";
  const bandColor = score >= 70 ? C.success : score >= 40 ? C.warn : C.danger;

  // Recommendation logic (Hardcoded for stability, maps 0-4 index to a recommendation)
  const RECOMMENDATIONS = [
    {
      label: "Diagnose",
      why: "Assess risk, cash needs, and structural stability.",
    },
    {
      label: "Design",
      why: "Translate priorities into design rules and target buckets.",
    },
    {
      label: "Deploy",
      why: "Codify discipline with an IPS and a review cadence.",
    },
    {
      label: "Review",
      why: "Test the plan's resilience against current market shocks.",
    },
    {
      label: "Clarify",
      why: "Close the loop and re-center goals for the new cycle.",
    },
  ];

  const currentRec = RECOMMENDATIONS[activeStepIndex % 5];
  const nextFocusHint = currentRec.why;

  return (
    <div
      aria-label="Sea-State Readiness"
      style={{
        background: C.card,
        border: `1px solid ${C.line}`,
        borderRadius: 20,
        boxShadow: SHADOW,
        padding: "18px 20px",
      }}
    >
      {/* Header and Gauge components render correctly */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 8,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, color: C.ink }}>
          Sea-State Readiness
        </h3>
        <div
          style={{
            fontSize: 12,
            color: bandColor,
            background: C.brand10,
            border: `1px solid ${bandColor}`,
            borderRadius: 999,
            padding: "2px 8px",
          }}
          aria-live="polite"
        >
          {band} • {Math.round(score)}%
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "320px 1fr",
          gap: "1.25rem",
          alignItems: "center",
        }}
      >
        {/* Gauge */}
        <div style={{ justifySelf: "center" }}>
          <Gauge score={score} />
        </div>

        {/* Controls */}
        <div>
          <div
            style={{
              fontSize: 12,
              color: C.ink70,
              marginBottom: 6,
              letterSpacing: ".02em",
            }}
          >
            CHECKLIST
          </div>
          <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
            <CheckItem
              label="Investment Policy Statement is in writing"
              checked={ips}
              onChange={setIps}
            />
            <CheckItem
              label="6–12 months cash runway set aside"
              checked={cash}
              onChange={setCash}
            />
            <CheckItem
              label="Estate documents and beneficiaries current"
              checked={estate}
              onChange={setEstate}
            />
            <CheckItem
              label="Decisions follow a quarterly cadence (not headlines)"
              checked={cadence}
              onChange={setCadence}
            />
          </div>

          <div
            style={{ height: 1, background: C.line, margin: "10px 0 12px" }}
          />

          <div
            style={{
              fontSize: 12,
              color: C.ink70,
              marginBottom: 6,
              letterSpacing: ".02em",
            }}
          >
            SIMULATION
          </div>
          <Slider
            label={`Market drop: ${drop}%`}
            min={0}
            max={40}
            step={1}
            value={drop}
            onChange={setDrop}
          />
          <Slider
            label={`Income disruption: ${incomeGap} mo`}
            min={0}
            max={6}
            step={1}
            value={incomeGap}
            onChange={setIncomeGap}
          />

          {/* Recommendation */}
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              background: C.brand10,
              border: `1px solid ${C.line}`,
              borderRadius: 12,
              color: C.ink,
              fontSize: 13,
            }}
          >
            <strong style={{ color: C.ink }}>
              Next focus ({currentRec.label}):
            </strong>{" "}
            {nextFocusHint}
          </div>
        </div>
      </div>
    </div>
  );
}

// FIX: Export the function definition to satisfy React's requirement
export default SeaStateReadiness;
